import React, { useState, useEffect } from "react";
import SEO from "../seo";
const ManageCustomer = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "customer-accounts-overview",
        "how-customer-accounts-are-created",
        "inviting-customers-to-activate-accounts",
        "managing-customer-accounts",
        "registration-options",
        "login-options",
        "password-management",
        "faqs",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Manage Customer Accounts | xPage Help Center"
        description="Learn how to manage customer accounts in xPage. Overview, registration, login options, password management, and FAQs for efficient account management."
        keywords="xPage, manage customer accounts, customer registration, login options, password management, FAQs, account activation, invite customers"
        canonical="https://help.xpage.ai/help/store-operation/Manage%20Customer"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Manage Customer Accounts
          </h1>
          <p className="text-sm text-systemLabel">
            xPage allows you to efficiently manage all customer accounts,
            providing a complete view of personal information, order history,
            and account activity. This ensures you can deliver personalized
            experiences and maintain accurate records.
          </p>
        </header>

        {/* Customer Accounts Overview Section */}
        <section id="customer-accounts-overview" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Customer Accounts Overview
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Customer accounts store key information collected through
              registration, orders, and other interactions with your store. This
              includes:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li>Customer ID, name, email, phone number, and address</li>
              <li>Login credentials and password</li>
              <li>Customer tags and tax exemption settings</li>
              <li>Notes and internal comments</li>
            </ul>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">TIP:</span> Email addresses and
                phone numbers are treated as unique identifiers in
                Xpage—duplicates are not allowed.
              </p>
            </div>
          </div>
        </section>

        {/* How Customer Accounts Are Created Section */}
        <section
          id="how-customer-accounts-are-created"
          className="mb-6 scroll-mt-20"
        >
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            How Customer Accounts Are Created
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Accounts in Xpage can be created through several channels:
            </p>

            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Channel
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    <strong className="text-systemTitle text-sm">
                      Description
                    </strong>
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Customer Registration
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Accounts are automatically created when a customer registers
                    and logs in.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Guest Orders
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    A customer placing an order generates an account using their
                    email or phone number, initially marked as pending
                    activation.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Email Marketing Subscription
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Accounts are created when a customer subscribes to marketing
                    communications.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Other Data Submissions
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Information submitted via pop-ups, forms, or inbox messages
                    also generates accounts with pending activation status.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Inviting Customers to Activate Accounts Section */}
        <section
          id="inviting-customers-to-activate-accounts"
          className="mb-6 scroll-mt-20"
        >
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Inviting Customers to Activate Accounts
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Activation verifies the customer's email or phone number and
              unlocks full account features.
            </p>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps to Invite Activation:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li className="mb-2">
                <span className="font-medium">Go to Xpage → Customers.</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Open the Customer Details page and click Send Invitation.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Review the email template or customize it as needed.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Once the customer receives the email, they click Activate
                  Account, set a password, and complete activation.
                </span>
              </li>
            </ol>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">TIP:</span> If the customer
                declines activation, their status will show as Customer declined
                activation. Past invitations and timestamps are tracked for
                reference.
              </p>
            </div>
          </div>
        </section>

        {/* Managing Customer Accounts Section */}
        <section id="managing-customer-accounts" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Managing Customer Accounts
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              This section covers the various aspects of managing customer
              accounts in xPage.
            </p>
          </div>
        </section>

        {/* Registration Options Section */}
        <section id="registration-options" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Registration Options
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Customers can create accounts using:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                <strong>Email + Password:</strong> Enter an email and a password
                (minimum 6 characters).
              </li>
              <li>
                <strong>Third-Party Accounts:</strong> Quick registration via
                Google or Facebook.
              </li>
            </ul>
          </div>
        </section>

        {/* Login Options Section */}
        <section id="login-options" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Login Options
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Xpage supports multiple login methods:
            </p>

            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Method</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    <strong className="text-systemTitle text-sm">
                      Description
                    </strong>
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Email + Password
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Standard login with registered credentials.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Third-Party Accounts
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Log in via Google or Facebook accounts. New users are
                    automatically registered.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Verification Code
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Customers can log in by receiving a one-time verification
                    code via email.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Password Management Section */}
        <section id="password-management" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Password Management
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Customer Actions:
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li>
                <strong>Change Password:</strong> My Profile → Edit Profile →
                Change Password.
              </li>
              <li>
                <strong>Forgot Password:</strong> Login page → Forgot Password.
              </li>
            </ul>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Merchant Actions:
            </h3>
            <p className="text-sm text-systemLabel mb-4">
              <strong>Reset Customer Password:</strong> In the Xpage admin, open
              the Customer Details page → More Actions → Reset Account Password.
              A reset email is sent to the customer.
            </p>
          </div>
        </section>

        {/* FAQs Section */}
        <section id="faqs" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">FAQs</h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-systemTitle mb-2">
                Q1: Didn't receive the verification code?
              </h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
                <li>Ensure the email address is correct.</li>
                <li>Wait 60 seconds and request a new code if needed.</li>
                <li>
                  Verify that the sender email is properly configured in Store
                  Notifications.
                </li>
                <li>Contact Xpage support if the issue persists.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-systemTitle mb-2">
                Q2: Verification code login failed?
              </h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
                <li>
                  Check that the code is within the 15-minute validity period.
                </li>
                <li>If expired, request a new code.</li>
                <li>Contact Xpage support if problems continue.</li>
              </ul>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#customer-accounts-overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "customer-accounts-overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Customer Accounts Overview
              </a>
            </li>
            <li>
              <a
                href="#how-customer-accounts-are-created"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "how-customer-accounts-are-created"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                How Accounts Are Created
              </a>
            </li>
            <li>
              <a
                href="#inviting-customers-to-activate-accounts"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "inviting-customers-to-activate-accounts"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Inviting Activation
              </a>
            </li>
            <li>
              <a
                href="#managing-customer-accounts"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "managing-customer-accounts"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Managing Accounts
              </a>
            </li>
            <li>
              <a
                href="#registration-options"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "registration-options"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Registration Options
              </a>
            </li>
            <li>
              <a
                href="#login-options"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "login-options"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Login Options
              </a>
            </li>
            <li>
              <a
                href="#password-management"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "password-management"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Password Management
              </a>
            </li>
            <li>
              <a
                href="#faqs"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "faqs"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                FAQs
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ManageCustomer;
